import { Component, OnInit, ViewChild } from '@angular/core';
import { Modal } from '@app/core/types/modal-type';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormApiService } from '@app/core/services/form-api.service';
import {
  CreateMeteringPointCommand,
  MeteringPointService,
  MeteringPointsService,
  MeteringPointType,
  UpdateMeteringPointCommand
} from '@app/api/dmc';
import { logFormErrors } from '@app/core/utils/log-form-errors';
import { ControlsOf, FormGroupType } from '@app/core/utils/formgroup-type';
import { createInstallationLocationAddressForm } from '@shared/forms/installation-location-adderss.formgroup';
import { createDetailCharacteristicsForm } from '@shared/forms/detail-characteristics.formgroup';
import {
  MeteringpointFormInstallationComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-installation/meteringpoint-form-installation.component';
import {
  MeteringpointFormCreationDetailsComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-creation-details/meteringpoint-form-creation-details.component';
import {
  MeteringpointFormReadingBillingComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-reading-billing/meteringpoint-form-reading-billing.component';
import {
  MeteringpointFormProductionComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-production/meteringpoint-form-production.component';
import {
  MeteringpointFormAddressComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-address/meteringpoint-form-address.component';
import { createDetailMeterCharacteristicsForm } from '@shared/forms/detail-meter-characteristics.formgroup';
import { isoToRealDate } from '@app/core/utils/iso-to-real-date';
import { FormConfig } from '@shared/interfaces/form-configs/form-config';
import { meteringPointTypeConfig } from '@views/meteringpoint/configs/meteringpointtype-config';
import { applyFormConfig } from '@app/core/utils/apply-form-config';
import { GuideCardComponent } from '@shared/components/guide-card/guide-card.component';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { ModalService } from '@app/core/services/modal.service';
import {
  FindMeteringpointModalComponent
} from '@shared/modals/meteringpoint/find-meteringpoint-modal/find-meteringpoint-modal.component';
import {
  MeteringpointFormExchangeComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-exchange/meteringpoint-form-exchange.component';
import { InputDirective } from '@root/app/shared/components/input/input/input.directive';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-edit-meteringpoint-modal',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    GuideCardComponent,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    MeteringpointFormInstallationComponent,
    MeteringpointFormCreationDetailsComponent,
    MeteringpointFormReadingBillingComponent,
    MeteringpointFormProductionComponent,
    MeteringpointFormExchangeComponent,
    MeteringpointFormAddressComponent
  ],
  templateUrl: './edit-meteringpoint-modal.component.html',
  styleUrl: './edit-meteringpoint-modal.component.css'
})
export class EditMeteringpointModalComponent implements Modal, OnInit, HasUnsavedChanges {
  @ViewChild('productionForm') private readonly productionForm?: MeteringpointFormProductionComponent;

  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public form: FormGroupType<UpdateMeteringPointCommand> = new FormGroup<ControlsOf<UpdateMeteringPointCommand>>({
    identification: new FormControl<string | null>(null, Validators.required),
    validFrom: new FormControl(null, Validators.required),
    parentMeteringPointIdentification: new FormControl<string | null>(null, [Validators.minLength(18), Validators.maxLength(18)]),
    connectionPointId: new FormControl(),
    installationLocationAddress: createInstallationLocationAddressForm(),
    detailCharacteristics: createDetailCharacteristicsForm(),
    detailMeterCharacteristic: createDetailMeterCharacteristicsForm(),
    // @ts-ignore-next-line
    id: new FormControl(),
  });

  /** ID **/
  public id!: string;

  /**
   * Value Carriers
   */
  protected readonly MeteringPointType = MeteringPointType;

  /** Configs **/
  public configs: Partial<Record<MeteringPointType, FormConfig<CreateMeteringPointCommand>>> = meteringPointTypeConfig;

  constructor(
    private readonly formApiService: FormApiService,
    private readonly meteringPointService: MeteringPointService,
    private readonly meteringPointsService: MeteringPointsService,
    private readonly modalService: ModalService,
  ) {
  }

  public ngOnInit(): void {
    if (!this.id) {
      console.warn('Missing ID for EditMeteringpointModalComponent');
      return;
    }

    /** Apply Config if available **/
    this.form.get('detailCharacteristics.meteringPointType')?.valueChanges.subscribe((type: MeteringPointType) => {
      if (type && this.configs[type]) {
        applyFormConfig<CreateMeteringPointCommand>(this.form, this.configs[type]);
      }
    })


    this.fetchMeteringPointFromId(this.id);
  }

  private fetchMeteringPointFromId(id: string): void {
    this.meteringPointService.meteringPointGetByIdentificationGet(id).subscribe((result) => {
      this.form.patchValue(result as UpdateMeteringPointCommand);
      this.productionForm?.rebuildNetSettlementGroupList();

      let d = isoToRealDate(new Date());

      this.form.get('validFrom')?.setValue(d);

      if (this.form.get('detailCharacteristics.meteringPointType')?.value === MeteringPointType.Exchange) {
        this.form.get('detailCharacteristics.toGridAreaId')?.setValue(result.detailCharacteristics?.toGrid?.id);
        this.form.get('detailCharacteristics.fromGridAreaId')?.setValue(result.detailCharacteristics?.fromGrid?.id);
      }

      this.form.get('detailMeterCharacteristic')?.disable();

    })
  }

  public submit(): void {
    this.form.get('connectionPointId')?.disable();

    if (!this.form.valid) {
      logFormErrors(this.form);
      return;
    }

    // Address washing populates a disabled DAR field; include it when serializing the form.
    this.form.get('installationLocationAddress')?.enable();

    /** Disable fields **/
    this.form.get('installationLocationAddress.washInstructions')?.disable();


    this.formApiService.submit(
      this.form,
      (data: any) => this.meteringPointsService.meteringPointsIdPut(data.id, data),
      {
        closeModal: true,
        successMessage: 'meteringPoint.edit.success'
      }
    )
  }

  /** Modals **/
  public openMeteringPointModal(): void {
    this.modalService.show({
      component: FindMeteringpointModalComponent,
      title: 'meteringPoint.find.modal.title',
      size: 'xl',
      actions: [
        { type: 'primary', label: 'common.select', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ]
    }).subscribe((meteringPointId: string) => {
      this.form.get('parentMeteringPointIdentification')?.patchValue(meteringPointId);
    })
  }

}
